// RetentionCurves.tsx — operator visuals G6: how long viewers stayed, per published post, as a line chart (retained
// share vs seconds) instead of numbers. Data: CMS cms_retention_curve through the ops proxy. Nothing shown until a
// platform has reported a curve (YouTube Analytics lags a day or more; TikTok offers none).
import { useEffect, useState } from "react";
import { opsApi } from "../../api/ops.ts";
import { videosApi } from "../../api/videos.ts";
import { curvePath } from "./retention-path.ts";

type Curve = { platform: string; postId: string; points: { second: number; retained: number }[] };

const COLOURS: Record<string, string> = { youtube: "#dc2626", tiktok: "#111827" };

/** The operator's (by CMS document, through the ops proxy) or (tf3-34b) the customer's own video's (by video id, through
 *  GET /api/videos/:id/retention-curve). `emptyText`: said once read and no platform has reported a curve yet. */
export function RetentionCurves({ cmsId = null, videoId, emptyText }: { cmsId?: string | null; videoId?: string; emptyText?: string }) {
  const [curves, setCurves] = useState<Curve[] | null>(null);
  const [readErr, setReadErr] = useState<string | null>(null);
  // (tf3-31, A17) a failed read is said, never "no curve reported yet"
  useEffect(() => {
    const read = videoId ? () => videosApi.retentionCurve(videoId) : cmsId ? () => opsApi.retentionCurve(cmsId) : null;
    if (read) { setReadErr(null); read().then((r) => setCurves(r.curves))
      .catch((e) => setReadErr(String((e as Error)?.message ?? e))); }   // catch-reason: the error is shown in place of the curves
  }, [cmsId, videoId]);
  if (readErr) return <div style={{ margin: "10px 0", fontSize: 12, color: "#b91c1c" }}>The retention curves couldn't be read: {readErr}</div>;
  if (!curves?.length) return curves && emptyText ? <p className="tf-muted" style={{ fontSize: 12, margin: "10px 0" }}>{emptyText}</p> : null;
  const W = 360, H = 140;
  const maxSec = Math.max(...curves.flatMap((c) => c.points.map((p) => p.second)), 1);
  return (
    <div style={{ margin: "10px 0", padding: 10, background: "#f9fafb", borderRadius: 8, display: "inline-block" }} data-visual>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Retention — share still watching</div>
      <svg width={W} height={H + 18} role="img" aria-label="retention curves">
        {[0.25, 0.5, 0.75].map((y) => <line key={y} x1={0} x2={W} y1={(1 - y) * H} y2={(1 - y) * H} stroke="#e5e7eb" />)}
        {curves.map((c) => <path key={`${c.platform}:${c.postId}`} d={curvePath(c.points, maxSec, W, H)} fill="none" stroke={COLOURS[c.platform] ?? "#2563eb"} strokeWidth={2} />)}
        <text x={0} y={H + 14} fontSize={10} fill="#6b7280">0s</text>
        <text x={W - 24} y={H + 14} fontSize={10} fill="#6b7280">{Math.round(maxSec)}s</text>
      </svg>
      <div style={{ display: "flex", gap: 10, fontSize: 11 }}>
        {curves.map((c) => <span key={`${c.platform}:${c.postId}`} style={{ color: COLOURS[c.platform] ?? "#2563eb" }}>● {c.platform} · {c.points.at(-1) ? `${Math.round(c.points.at(-1)!.retained * 100)}% at the end` : ""}</span>)}
      </div>
    </div>
  );
}
